import _ from 'lodash';
import moment from 'moment';
import { fromJS } from 'immutable';
import {
  Button,
  Row,
  Col,
  Form,
  Input,
  Select,
  Spin,
  Switch,
  Tag,
  Alert,
  Popover,
  Table,
} from 'antd';

import React, { PureComponent } from 'react';
import { connect } from 'react-redux';

import { addConstituent, fetchTags, patchConstituent } from '../../../actions';

import { connectForm } from '../../form';

import { ErrorsList } from '../../ErrorsList';
import { ConstituentTagList } from '../../ConstituentTagList';
import {
  fullTagLabel, reduceTagsHierarchy,
} from '../../../utils';
import { getPhoneNumberField } from '../../fields/PhoneNumberField';
import getDateOfBirthField, { generateBirthDateTooltip } from '../../fields/DateOfBirthField';
import { IconDefinedTerm, PortalChild } from '../../elements';

const LABEL_COL_WIDTH = 5;
const WRAPPER_COL_WIDTH = 24 - LABEL_COL_WIDTH;

const NAME_ITEM_LAYOUT = {
  wrapperCol: {
    sm: { span: 19 },
    md: { span: 19 },
    lg: {
      span: 17,
      offset: 0,
    },
  },
  labelCol: {
    sm: { span: 5 },
    md: { span: 5 },
    lg: {
      span: 5,
      offset: 2,
    },
  },
};

const ADD_ALT_NAME_ITEM_LAYOUT = {
  wrapperCol: {
    sm: { span: 18 },
    md: { span: 18 },
    lg: {
      span: 1,
      offset: 0,
    },
  },
  labelCol: {
    sm: { span: 5 },
    md: { span: 5 },
    lg: {
      span: 18,
      offset: 0,
    },
  },
};

const getProcessedData = data => {
  const bDate = data.birthDate;
  const birthDate = (bDate && bDate !== null) ? bDate.format('YYYY-MM-DD') : null;
  const alternateName = data.addAlternateName ? data.alternateName : null;
  const { positionNumber } = data;
  return { ...data, birthDate, alternateName, positionNumber };
};

class ExternalLinkFauxInput extends PureComponent {
  render() {
    const { template, value } = this.props;
    if (!(value && template)) return null;
    const link = template.replace(/\[ID\]/g, value);
    return <a target="_blank" rel="noopener noreferrer" href={link}>{link}</a>;
  }
}

@connect(
  state => ({
    organizationId: state.doc.getIn(['authMr', 'organizationId'], null),
    organization: state.doc.getIn(['authMr', 'constituent', 'organization'], null),
    orgRootTag: state.doc.getIn(['tagsMr', 'tree', 'organization'], null),
    allTags: state.doc.getIn(['tagsMr', 'items'], fromJS([])).toJS(),
  }),
  {
    fetchTags,
    addConstituent,
    patchConstituent,
  },
)
@connectForm(({
  isNew,
  constituent,
  organizationId,
  addConstituent,
  patchConstituent,
}) => ({
  httpGroup: isNew ? 'addConstituent' : 'patchConstituent',
  postAction: isNew
    ? data => addConstituent({
      organization: {
        id: organizationId,
      },
      ...getProcessedData(data),
    })
    : data => patchConstituent(constituent.get('id'), getProcessedData(data)),
  transformToServer: d => ({
    ...d,
    directTags: d.directTags.map(t => ({ id: t })),
  }),
}))
class ConstituentDetailsForm extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showAlternateName: false,
      showAlternateNameWarning: false,
    };
  }

  handleAddAlternateNameChange = checked => {
    this.setState({ showAlternateName: checked });
    const alternateName = this.props.constituent && this.props.constituent.get('alternateName', null);
    if (!checked) this.props.form.setFieldsValue({ alternateNumber: null });
    if (alternateName && !checked) {
      this.setState({ showAlternateNameWarning: true });
    } else {
      this.setState({ showAlternateNameWarning: false });
    }
  };

  renderNudgeNotificationTable = () => {
    const { nudgeNotifications, nudgeNotificationsLoading, constituentId } = this.props;
    if (!constituentId) return null;

    let dataSource = [];
    let enabled = false;
    if (nudgeNotifications) {
      let data = nudgeNotifications;
      if (typeof data.toJS === 'function') {
        data = data.toJS();
      }
      if (Array.isArray(data)) {
        dataSource = data;
      } else if (data && Array.isArray(data.items)) {
        enabled = !!data.enabled;
        dataSource = data.items;
      }
    }

    if (!enabled) return null;

    const columns = [
      {
        title: 'Reference',
        dataIndex: 'icrId',
        key: 'icrId',
        render: text => text ?? '-',
      },
      {
        title: 'ICR Creation Date',
        dataIndex: 'icrCreatedAt',
        key: 'icrCreatedAt',
        render: text => (text ? moment(text).format('YYYY-MM-DD HH:mm') : '-'),
      },
      {
        title: 'Current Nudge Count',
        dataIndex: 'nudgeCount',
        key: 'nudgeCount',
        render: text => text ?? 0,
      },
      {
        title: 'Last Nudge Sent',
        dataIndex: 'lastNudgeSentAt',
        key: 'lastNudgeSentAt',
        render: text => (text ? moment(text).format('YYYY-MM-DD HH:mm') : '-'),
      },
      {
        title: 'Remaining Nudges',
        dataIndex: 'remainingNudges',
        key: 'remainingNudges',
        render: text => text ?? '-',
      },
      {
        title: 'Next Scheduled Nudge',
        dataIndex: 'nextScheduledNudge',
        key: 'nextScheduledNudge',
        render: text => (text ? moment(text).format('YYYY-MM-DD HH:mm') : '-'),
      },
    ];

    return (
      <div style={{ marginTop: '24px', marginBottom: '24px' }}>
        <h4>Nudge Notification Status</h4>
        <Table
          dataSource={dataSource}
          columns={columns}
          rowKey={(record, index) => record.icrId || index}
          pagination={false}
          size="small"
          loading={nudgeNotificationsLoading}
          locale={{ emptyText: 'No nudge notifications' }}
        />
      </div>
    );
  };

  render() {
    const {
      constituent,
      form,
      constituentId,
      organizationId,
      allTags,
      onCancel,
      saveInProgress,
      handleSubmit,
      isNew,
      organization,
      isEditable = true,
    } = this.props;

    const { showAlternateName, showAlternateNameWarning } = this.state;

    const { getConnectedFieldDecorator } = form;

    const { externalIdEnabled, externalLinkTpl, managerEmailEnabled } = organization ? organization.toJS() : {};
    const formItemProps = {
      labelCol: { span: LABEL_COL_WIDTH },
      wrapperCol: { span: WRAPPER_COL_WIDTH },
    };

    const orgRootTag = this.props.orgRootTag ? this.props.orgRootTag.toJS() : null;
    const orgTag = ((orgRootTag || {}).children || []).find(
      tag => tag.role === `organization.${organizationId}`,
    );

    const tagsListJs = orgTag ? reduceTagsHierarchy(orgTag, false) : [];

    // Map the constituent tag list from the global tag list
    const constituentDirectTagsRaw = constituent ? constituent.get('directTags', null) : null;
    const directTags = (constituentDirectTagsRaw ? constituentDirectTagsRaw.toJS() : [])
      .map(({ id }) => allTags.find(tag => tag.id === id));

    const selectableTags = tagsListJs ? tagsListJs.map(tag => ({
      optValue: fullTagLabel(tag, { joiner: ' ' }),
      optLabel: fullTagLabel(tag),
      ...tag,
    })) : [];

    const selectedTags = directTags.filter(tag => tag && tag.editable);
    const appliedNonEditableTags = directTags.filter(tag => tag && !tag.editable);

    if (!isNew && constituentId && !constituent) {
      return <Spin spinning={true} />;
    }

    const initialData = constituent ? constituent.toJSON() : {
      active: true,
    };

    let emailWarning = null;
    if (constituent) {
      const userEmail = constituent.getIn(['user', 'email']);
      const constituentEmail = constituent.get('email');
      if (userEmail && userEmail !== constituentEmail) {
        emailWarning = (<div>Login email is different. Use <strong>{constituent.getIn(['user', 'email'])}</strong></div>);
      }
    }

    const alternateName = (constituent && constituent.get('alternateName', null)) || showAlternateName;

    initialData.directTags = !selectedTags ? [] : selectedTags.map(tag => tag.id);

    return (<Form ref="form" onSubmit={this.handleSubmit}>
      <ErrorsList errors={this.props.globalErrors} />
      <Form.Item
        label="Active"
        extra="Is this person actively working for your organisation?"
        {...formItemProps}
      >
        {getConnectedFieldDecorator('active', {
          initialValue: initialData.active,
          valuePropName: 'checked',
        })(
          <Switch disabled={!isEditable} />,
        )
        }
      </Form.Item>
      <Row gutter={16}>
        <Col xs={24} sm={24} md={24} lg={18}>
          <Form.Item
            label={(<IconDefinedTerm definition="Needs to match name that the person's accreditation is issued under">
          Full name
            </IconDefinedTerm>)}
            {...formItemProps}
            {...NAME_ITEM_LAYOUT}
          >
            {getConnectedFieldDecorator('fullName', {
              initialValue: initialData.fullName,
              backendValidateLinks: ['email', 'mobileNumber'],
              rules: [
                {
                  required: true,
                  whitespace: true,
                  message: 'Full name is required',
                },
              ],
            })(
              <Input disabled={!isEditable}/>,
            )
        }
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={24} lg={6}>
          <Form.Item
            label={(<IconDefinedTerm definition="This person has an alternate legal name (maiden, married, legally changed)">
        Alternate name
            </IconDefinedTerm>)}
            {...formItemProps}
            {...ADD_ALT_NAME_ITEM_LAYOUT}
          >
            {getConnectedFieldDecorator('addAlternateName', {
              initialValue: !!alternateName,
              valuePropName: 'checked',
              onChange: this.handleAddAlternateNameChange,
            })(
              <Switch disabled={!isEditable}/>,
            )
        }
          </Form.Item>
        </Col>
      </Row>
      {showAlternateNameWarning && (
      <span style={{ color: 'red', display: 'flex', justifyContent: 'center' }}>
              Disabling the 'Alternate name' switch will remove the existing alternate name upon saving
      </span>
      )}
      {
        showAlternateName && (
          <strong style={{ color: '#C88242', display: 'flex', justifyContent: 'center' }}>
                When adding alternate name, you confirm diligence checks on correctness and evidence to support
          </strong>
        )
      }
      { alternateName && <Form.Item
        label={'Alternate name'}
        {...formItemProps}
                         >
        {getConnectedFieldDecorator('alternateName', {
          initialValue: initialData.alternateName,
          rules: [
            {
              required: true,
              whitespace: true,
              message: 'Alternate name is required',
            },
          ],
        })(
          <Input disabled={!isEditable}/>,
        )
        }
      </Form.Item>}
      <Spin spinning={_.isNil(orgRootTag)}>
        <Form.Item
          label={(<IconDefinedTerm definition="Tags enable admin users to organise people into groups.">Tags</IconDefinedTerm>)}
          extra={(
            <ConstituentTagList selectedTags={fromJS(appliedNonEditableTags)} />
          )}
          {...formItemProps}
        >
          {getConnectedFieldDecorator('directTags', {
            initialValue: initialData.directTags,
          })(
            <Select
              mode="multiple"
              placeholder="No tags"
              disabled={!isEditable}
              // ref="tags"
              key={selectedTags ? 'withValues' : 'noValues'}
              optionFilterProp="filtervalue"
            >
              {selectableTags.map(tag => (<Select.Option
                key={tag.id}
                value={tag.id}
                filtervalue={tag.optValue}
                                          >
                { tag.optLabel}
              </Select.Option>))}
            </Select>,
          )}
        </Form.Item>
      </Spin>
      { /* TODO read only if has a user */}
      <Form.Item
        label="Email address"
        extra={emailWarning}
        {...formItemProps}
      >
        {getConnectedFieldDecorator('email', {
          initialValue: initialData.email || '',
          backendValidateLinks: ['fullName', 'mobileNumber'],
          validate: [{
            trigger: 'onBlur',
            rules: [{
              type: 'email',
              message: 'Email address is invalid',
            }],
          }],
        })(
          <Input disabled={!isEditable}/>,
        )
        }
      </Form.Item>
      <Form.Item
        label="Mobile number"
        {...formItemProps}
      >
        {getPhoneNumberField({
          initialValue: initialData.mobileNumber || '',
          fieldName: 'mobileNumber',
          fieldText: 'Mobile number',
          isDisabled: !isEditable,
          rules: [
            { validator: this.backendErrorsValidator },
          ],
          form,
        })}
      </Form.Item>
      <Form.Item label="Alternate number" {...formItemProps}>
        {getPhoneNumberField({
          initialValue: initialData.alternateNumber || '',
          fieldName: 'alternateNumber',
          fieldText: 'Alternate number',
          isDisabled: !isEditable,
          rules: [
            { validator: this.backendErrorsValidator },
          ],
          form,
        })}
      </Form.Item>
      <Form.Item
        label={(<IconDefinedTerm definition={generateBirthDateTooltip(initialData.birthDate)}>
          Date of Birth
        </IconDefinedTerm>)}
        {...formItemProps}
      >
        {getConnectedFieldDecorator('birthDate', {
          initialValue: (initialData.birthDate && initialData.birthDate !== null) ? moment(initialData.birthDate) : null,
        })(
          getDateOfBirthField({ initialValue: initialData.birthDate, yearsToRestrict: 10, disabled: !isEditable }),
        )
        }
      </Form.Item>

      { externalIdEnabled ? [
        <Form.Item
          label="External ID"
          key="id"
          {...formItemProps}
        >
          {getConnectedFieldDecorator('externalId', {
            initialValue: initialData.externalId || '',
          })(
            <Input disabled={!isEditable}/>,
          )
          }
        </Form.Item>,
        externalLinkTpl && form.getFieldValue('externalId')
          ? <Form.Item label="External Link" key="link" {...formItemProps}>
            {getConnectedFieldDecorator('externalId', {
            })(
              <ExternalLinkFauxInput template={externalLinkTpl} />,
            )
            }
          </Form.Item> : null,
      ] : null}
      { managerEmailEnabled
        && <Form.Item
          label="Manager email"
          {...formItemProps}
           >
          {getConnectedFieldDecorator('manager_email', {
            initialValue: initialData.managerEmail || '',
            backendValidateLinks: ['fullName', 'mobileNumber'],
            validate: [{
              trigger: 'onBlur',
              rules: [{
                type: 'email',
                message: 'Email address is invalid',
              }],
            }],
          })(
            <Input disabled={!isEditable}/>,
          )
          }
        </Form.Item>
      }
      <Form.Item label="Position Number" {...formItemProps}>
        {getConnectedFieldDecorator('positionNumber', {
          initialValue: initialData.positionNumber || '',
        })(
          <Input placeholder="Enter position number" disabled={!isEditable}/>,
        )}
        {form.getFieldValue('positionNumber') && (
          <div style={{ fontSize: '11px', color: '#888' }}>
            <Spin spinning={this.props.loadingRequiredChecks} size="small">
              {(this.props.requiredChecks || []).length > 0 ? (
                <div style={{ lineHeight: '14px', display: 'flex', gap: '12px' }}>
                  Required accreditations: {(this.props.requiredChecks || []).length}
                  <Popover
                    content={
                      <div style={{ maxWidth: '300px' }}>
                        <ul style={{ paddingLeft: '20px', margin: '0' }}>
                          {(this.props.requiredChecks || []).map((check, index) => (
                            <li key={index}>{check.type_name}</li>
                          ))}
                        </ul>
                      </div>
                    }
                    title="Required Accreditations"
                    trigger="click"
                    placement="topLeft"
                  >
                    <a style={{ fontSize: '11px', cursor: 'pointer' }}>View requirements</a>
                  </Popover>
                </div>
              ) : (
                this.props.loadingRequiredChecks ? 'Loading requirements...' : 'No checks required'
              )}
            </Spin>
          </div>
        )}
      </Form.Item>

      {this.renderNudgeNotificationTable()}

      <PortalChild portal={this.props.footerRef}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          disabled={!isEditable}
          // Can't use HTML form submit as this is rendered outside the
          // HTML form
          type="primary"
          loading={saveInProgress}
        >Save</Button>
      </PortalChild>
    </Form>);
  }
}

export {
  ConstituentDetailsForm,
};
